"use client";

import { useState } from "react";
import Image, { ImageProps, ImageLoaderProps } from "next/image";
import { resolveImageDelivery, BLUR_PLACEHOLDER } from "@/lib/utils/image";
import { supabaseImageVariant } from "@/lib/supabase-image";

/**
 * Supabase Storage 사전 생성 변형(-w480/-w960.webp) loader.
 * 모듈 레벨에 두어 렌더마다 재생성되지 않도록 한다 (Image 재마운트 방지).
 *
 * - quality 는 무시한다 (변형은 업로드 시 이미 인코딩됨).
 * - srcset 의 여러 width 가 같은 파일(480/960/원본)로 모이는 건 의도된 동작.
 * - 960 초과 → 원본. 원본을 그대로 돌려주면 next/image 가 dev 에서
 *   "loader does not implement width" 경고를 내므로 `#w=orig` 프래그먼트를 붙인다.
 *   프래그먼트는 서버로 전송되지 않아 CDN/브라우저 캐시 키가 원본과 같다
 *   (width 별 쿼리를 붙이면 캐시가 쪼개지므로 쓰지 않는다).
 */
const supabaseVariantLoader = ({ src, width }: ImageLoaderProps) => {
  const url = supabaseImageVariant(src, width);
  return url === src && !src.includes("#") ? `${src}#w=orig` : url;
};

interface OptimizedImageProps
  extends Omit<ImageProps, "placeholder" | "blurDataURL"> {
  /**
   * Enable blur placeholder (default: true)
   * 로딩 중 blur 효과 표시 여부
   */
  enablePlaceholder?: boolean;
  /**
   * Force optimization even for pre-compressed images
   * true 면 Supabase 이미지도 Vercel 이미지 최적화(/_next/image)를 경유한다.
   */
  forceOptimize?: boolean;
}

/**
 * Optimized Image Component
 *
 * Next.js Image 컴포넌트의 최적화된 래퍼:
 * - Supabase Storage 이미지는 업로드 시 만들어 둔 변형 파일(-w480/-w960.webp,
 *   그 이상은 원본)을 custom loader 로 직접 서빙. Supabase 이미지 변환
 *   (/render/image, 원본당 과금)도 Vercel 최적화(/_next/image)도 미경유.
 *   srcset/sizes/priority/lazy/blur 는 그대로 동작한다.
 * - 변형 파일이 아직 없으면(백필 전·변형을 만들지 않는 업로드 경로) 첫 onError 에서
 *   원본 URL(unoptimized)로 전환한다. 호출부 onError 도 그대로 호출된다.
 * - 그 외(로컬 /public, blob/data, SVG, unsplash 등 외부 호스트)는 unoptimized.
 * - forceOptimize 만이 Vercel 최적화기(/_next/image)를 타는 유일한 경로.
 * - 기본적으로 blur placeholder 적용
 *
 * @example
 * <OptimizedImage src={product.image_url} alt="Product" fill />
 * <OptimizedImage src={externalUrl} alt="External" forceOptimize fill />
 * <OptimizedImage src={url} alt="Image" enablePlaceholder={false} fill />
 */
export function OptimizedImage({
  src,
  enablePlaceholder = true,
  forceOptimize = false,
  onError,
  ...props
}: OptimizedImageProps) {
  const srcString = typeof src === "string" ? src : "";
  // 실패한 src 를 기억(키 방식) — src 가 바뀌면 자동으로 변형 경로로 복귀.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  // 서빙 경로 결정은 resolveImageDelivery 한 곳에서 (테스트 대상).
  const delivery = resolveImageDelivery(srcString, forceOptimize);
  const useVariants =
    delivery === "supabase-variant" && failedSrc !== srcString;

  // SVG가 아닌 경우에만 placeholder 적용
  const placeholderProps =
    enablePlaceholder && !srcString.endsWith(".svg")
      ? {
          placeholder: "blur" as const,
          blurDataURL: BLUR_PLACEHOLDER,
        }
      : {};

  const handleError: ImageProps["onError"] = (event) => {
    if (useVariants) setFailedSrc(srcString);
    onError?.(event);
  };

  return (
    <Image
      src={src}
      loader={useVariants ? supabaseVariantLoader : undefined}
      unoptimized={delivery !== "vercel" && !useVariants}
      onError={handleError}
      {...placeholderProps}
      {...props}
    />
  );
}
